import { Outlet, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tags, ShoppingCart, Globe, Zap, Leaf } from 'lucide-react';
import { platformBranding } from '@/config/branding';

export default function AuthLayout() {
  return (
    <div className="min-h-screen flex bg-[#F5F1E8]">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Outlet />
        </motion.div>
      </div>

      {/* Right side - Branding (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#7FAF6B] via-[#6B9F54] to-[#5A8C43]">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A961] rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-8 max-w-lg">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Link to="/" className="inline-flex items-center gap-3 mb-8">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg">
                <Leaf size={40} className="text-white" />
              </div>
            </Link>
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-4xl font-bold text-white mb-4 drop-shadow-sm"
          >
            {platformBranding.platform.fullName}
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-xl text-white/90 mb-12"
          >
            Your Complete Business Suite
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { name: 'LabelForge', icon: Tags, desc: 'Beautiful Labels', color: 'text-orange-300' },
              { name: 'ShopFather', icon: ShoppingCart, desc: 'Product Management', color: 'text-orange-400' },
              { name: 'Storefront', icon: Globe, desc: 'HTML Generator', color: 'text-orange-400' },
              { name: 'Easy Setup', icon: Zap, desc: 'Ready in Minutes', color: 'text-yellow-300' },
            ].map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
                className="bg-white/15 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-left hover:bg-white/20 transition-colors shadow-lg"
              >
                <item.icon className={`${item.color} mb-2 drop-shadow-sm`} size={24} />
                <h3 className="font-semibold text-white text-sm">{item.name}</h3>
                <p className="text-xs text-white/80">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

